import React, { useState } from 'react';
import clsx from 'clsx';
import { CloseButton } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/16/solid';

/**
 * Linear-density sidebar pieces (#603 step 3). Kept identical in front-porch
 * (src/shell/) and member-portal (src/Components/Shell/) — edit both.
 *
 * Catalyst's SidebarItem fixes its own padding and type size, which is
 * right for Catalyst and too roomy for this, so these are ours — but each link is still wrapped in
 * Headless UI's CloseButton, the one thing SidebarItem does that matters:
 * on a phone the sidebar is a drawer, and following a link must close it.
 *
 * Colour: the canvas-fg tokens — near-white on the logo-blue light canvas,
 * Linear's muted grey on the dark one — so no `dark:` twins are needed.
 */

export const NavItem: React.FC<{
  to: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  current?: boolean;
  children: React.ReactNode;
  trailing?: React.ReactNode;
  /** Runs before navigating — e.g. selecting which home the page shows. */
  onClick?: () => void;
  /**
   * A full page load (a plain `<a href>`) instead of client-side routing,
   * for a path this app does not serve — e.g. `/internal/`, which the
   * ingress sends to another pod on the same host. A router `Link` would
   * land on this app's NotFound route.
   */
  reload?: boolean;
}> = ({ to, icon: Icon, current, children, trailing, onClick, reload }) => {
  const props = {
    onClick,
    'aria-current': current ? ('page' as const) : undefined,
    className: clsx(
      'group flex items-center gap-2 rounded-md px-2 py-1 text-[13px] font-medium leading-5 transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-canvas-fg-strong/60',
      current
        ? 'bg-canvas-fg-strong/10 text-canvas-fg-strong'
        : 'text-canvas-fg hover:bg-canvas-fg-strong/[0.06] hover:text-canvas-fg-strong'
    ),
  };
  const content = (
    <>
      {Icon && (
        <Icon
          className={clsx(
            'h-4 w-4 shrink-0',
            current ? 'text-canvas-fg-strong' : 'text-canvas-fg/80'
          )}
          aria-hidden
        />
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing}
    </>
  );
  return reload ? (
    <CloseButton as="a" href={to} {...props}>
      {content}
    </CloseButton>
  ) : (
    <CloseButton as={Link} to={to} {...props}>
      {content}
    </CloseButton>
  );
};

/**
 * An icon-only link, for the footer's settings gear. Same CloseButton
 * wrapping as NavItem, so following it closes the phone drawer too.
 */
export const NavIconLink: React.FC<{
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  current?: boolean;
}> = ({ to, icon: Icon, label, current }) => (
  <CloseButton
    as={Link}
    to={to}
    aria-label={label}
    title={label}
    aria-current={current ? 'page' : undefined}
    className={clsx(
      'shrink-0 rounded-md p-1.5 transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-canvas-fg-strong/60',
      current
        ? 'bg-canvas-fg-strong/10 text-canvas-fg-strong'
        : 'text-canvas-fg hover:bg-canvas-fg-strong/10 hover:text-canvas-fg-strong'
    )}
  >
    <Icon className="h-4 w-4" aria-hidden />
  </CloseButton>
);

const readCollapsed = (key: string): boolean => {
  try {
    return localStorage.getItem(`sidebar:${key}`) === 'collapsed';
  } catch {
    return false;
  }
};

/**
 * A collapsible group with a small muted heading — Linear's "Workspace ▾",
 * "Favorites ▾". Open/closed is remembered per browser; storage failing
 * just means it forgets.
 */
export const NavSection: React.FC<{
  id: string;
  title?: string;
  children: React.ReactNode;
}> = ({ id, title, children }) => {
  const [collapsed, setCollapsed] = useState(() => (title ? readCollapsed(id) : false));
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(`sidebar:${id}`, next ? 'collapsed' : 'open');
    } catch {
      // Storage unavailable: the state lasts for this page only.
    }
  };
  const listId = `sidebar-section-${id}`;
  return (
    <div className="mt-4 first:mt-0">
      {title && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-controls={listId}
          className="mb-0.5 flex w-full items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-canvas-fg/80 hover:text-canvas-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-canvas-fg-strong/60"
        >
          {title}
          <ChevronRightIcon
            className={clsx('h-3 w-3 transition-transform', !collapsed && 'rotate-90')}
            aria-hidden
          />
        </button>
      )}
      {/* `hidden` as a class, not just the attribute: `flex` sets
          display:flex, which beats the attribute's display:none at equal
          specificity, and the list never collapsed. */}
      <div id={listId} hidden={collapsed} className={clsx(collapsed ? 'hidden' : 'flex flex-col gap-px')}>
        {children}
      </div>
    </div>
  );
};
